import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminContest from "@/pages/admin/AdminContest";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminAds from "@/pages/admin/AdminAds";
import AdminAccessTab from "@/pages/admin/AdminAccessTab";
import AdminAuditTab from "@/pages/admin/AdminAuditTab";
import AdminVaultTab from "@/pages/admin/AdminVaultTab";

const ALL_TABS = [
  "stats", "users", "applications", "trash", "invites", "requests",
  "access", "listings", "disputes", "reports", "audit", "contest", "messages", "ads", "vault",
];
const SUPER_ONLY_TABS = new Set(["vault"]);

export default function Admin() {
  const { user: me } = useAuth();
  const isSuper = !!me?.is_super_admin;
  const TABS = useMemo(() => ALL_TABS.filter((t) => !SUPER_ONLY_TABS.has(t) || isSuper), [isSuper]);
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [trash, setTrash] = useState([]);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [accessCodes, setAccessCodes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditSalesOnly, setAuditSalesOnly] = useState(false);
  const [vault, setVault] = useState({ transactions: [], stats: null });
  const [vaultFilter, setVaultFilter] = useState({ user_id: "", status: "" });
  const [newAccess, setNewAccess] = useState({ code: "", max_uses: "", note: "" });
  const [siteConfig, setSiteConfig] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [newInvite, setNewInvite] = useState({ code: "", max_uses: 1 });
  const [confirmPurge, setConfirmPurge] = useState(null); // { id, username, email }
  const [rejectApp, setRejectApp] = useState(null); // {id, username}
  const [rejectReason, setRejectReason] = useState("");
  const [resolveDispute, setResolveDispute] = useState(null); // tx doc
  const [resolveForm, setResolveForm] = useState({ outcome: "completed", resolution: "" });

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadUsers = async () => {
    try { const { data } = await api.get("/admin/users", { params: { q: userQuery || undefined } }); setUsers(data.users || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadTrash = async () => {
    try { const { data } = await api.get("/admin/users/trash"); setTrash(data.users || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadInvites = async () => {
    try { const { data } = await api.get("/invites"); setInvites(data.invites || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadRequests = async () => {
    try { const { data } = await api.get("/invites/requests"); setRequests(data.requests || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadListings = async () => {
    try { const { data } = await api.get("/admin/listings"); setListings(data.listings || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadReports = async () => {
    try { const { data } = await api.get("/admin/reports"); setReports(data.reports || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadAccessCodes = async () => {
    try { const { data } = await api.get("/admin/access-codes"); setAccessCodes(data.codes || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadSiteConfig = async () => {
    try {
      // Admin endpoint includes internal settings (notify_new_users, etc.)
      const { data } = await api.get("/admin/site-config");
      setSiteConfig(data);
    } catch { /* fall back to public read for landing_mode only */
      try {
        const { data } = await api.get("/site-config");
        setSiteConfig(data);
      } catch (e) { toast.error(formatErr(e)); }
    }
  };
  const toggleNotifyNewUsers = async (next) => {
    try {
      const { data } = await api.patch("/admin/site-config", { notify_new_users: !!next });
      setSiteConfig(data);
      toast.success(next
        ? "New-user email notifications turned ON."
        : "New-user email notifications turned OFF.");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const setLandingMode = async (mode) => {
    if (mode === "public" && !window.confirm(
      "Switch to PUBLIC launch mode?\n\n" +
      "This removes the temporary \"Coming Soon\" splash — anonymous visitors to " +
      "hunt57.com will land directly on the HUNT57 home page with SIGN UP / SIGN IN " +
      "buttons. You can flip back to invite-only anytime."
    )) return;
    try {
      const { data } = await api.patch("/admin/site-config", { landing_mode: mode });
      setSiteConfig(data);
      toast.success(mode === "public" ? "🚀 Public launch mode ON. Visitors land on the HUNT57 home page." : "Back to invite-only. Coming-Soon splash re-enabled.");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadApplications = async () => {
    try { const { data } = await api.get("/admin/vendor-applications", { params: { status: "pending" } }); setApplications(data.applications || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadDisputes = async () => {
    try { const { data } = await api.get("/admin/transactions", { params: { status: "disputed" } }); setDisputes(data.transactions || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadAuditLog = async () => {
    try {
      const params = { limit: 200 };
      if (auditSalesOnly) params.sales_only = true;
      const { data } = await api.get("/admin/audit-log", { params });
      setAuditLog(data.events || []);
    }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadVault = async () => {
    try {
      const params = {};
      if (vaultFilter.user_id) params.user_id = vaultFilter.user_id;
      if (vaultFilter.status) params.status = vaultFilter.status;
      const { data } = await api.get("/admin/sales-vault", { params });
      setVault(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const promoteToAdmin = async (u) => {
    if (!window.confirm(`Promote @${u.username} to admin?\n\nThey'll gain moderation powers but NOT the Sales Vault.`)) return;
    try { await api.post(`/admin/users/${u.id}/promote-admin`); toast.success(`@${u.username} is now an admin`); loadUsers(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const demoteFromAdmin = async (u) => {
    if (!window.confirm(`Demote @${u.username} from admin?\n\nThey'll lose all admin access.`)) return;
    try { await api.post(`/admin/users/${u.id}/demote-admin`); toast.success(`@${u.username} demoted`); loadUsers(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const promoteToSuper = async (u) => {
    if (!window.confirm(`Promote @${u.username} to SUPER ADMIN?\n\nThey'll gain full access to the Sales Vault and can promote/demote other admins.`)) return;
    try { await api.post(`/admin/users/${u.id}/promote-super-admin`); toast.success(`@${u.username} is now a super admin`); loadUsers(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const demoteFromSuper = async (u) => {
    if (!window.confirm(`Revoke super-admin from @${u.username}?\n\nThey'll remain a regular admin.`)) return;
    try { await api.post(`/admin/users/${u.id}/demote-super-admin`); toast.success(`@${u.username} super-admin revoked`); loadUsers(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const deleteTransaction = async (tx) => {
    if (!window.confirm(`Permanently delete this sale record?\n\n@${tx.buyer_username} → @${tx.seller_username}\n$${Number(tx.sale_price||0).toFixed(2)}\n\nThis cannot be undone. If the sale was qualifying, the seller's counter will be decremented.`)) return;
    try { await api.delete(`/admin/transactions/${tx.id}`); toast.success("Transaction deleted"); loadVault(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const wipeUserHistory = async () => {
    const uid = vaultFilter.user_id.trim();
    if (!uid) { toast.error("Filter by a user id first, then wipe."); return; }
    if (!window.confirm(`WIPE ALL SALES HISTORY for user ${uid}?\n\nEvery transaction where they're buyer or seller will be permanently deleted. Their qualifying-sales counter will be adjusted.\n\nThis cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${uid}/sales-history`);
      toast.success(`Wiped ${data.deleted} transaction(s); reversed ${data.qualifying_reversed} qualifying sale(s)`);
      loadVault();
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => {
    if (tab === "stats") loadStats();
    if (tab === "users") loadUsers();
    if (tab === "trash") loadTrash();
    if (tab === "invites") loadInvites();
    if (tab === "requests") loadRequests();
    if (tab === "listings") loadListings();
    if (tab === "reports") loadReports();
    if (tab === "access") { loadAccessCodes(); loadSiteConfig(); }
    if (tab === "applications") loadApplications();
    if (tab === "disputes") loadDisputes();
    if (tab === "audit") loadAuditLog();
    if (tab === "vault") loadVault();
  }, [tab, auditSalesOnly]);

  const updateUser = async (userId, patch) => {
    try {
      await api.patch(`/admin/users/${userId}`, patch);
      toast.success("Updated");
      loadUsers();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const softDeleteUser = async (userId, username) => {
    if (!window.confirm(`Move @${username} to the trash?\n\nThey will be auto-purged after 30 days unless restored.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`@${username} moved to trash`);
      loadUsers();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const restoreUser = async (userId, username) => {
    try {
      await api.post(`/admin/users/${userId}/restore`);
      toast.success(`@${username} restored`);
      loadTrash();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const clearStrikes = async (userId, username, count) => {
    if (!window.confirm(`Clear ${count} active strike${count === 1 ? "" : "s"} on @${username}?\n\nThis restores their standing to Excellent and lifts strike-based auto-suspension. Strike history stays in the audit log.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${userId}/clear-strikes`);
      toast.success(`@${username}: ${data.cleared} strike${data.cleared === 1 ? "" : "s"} cleared`);
      loadUsers();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const permanentlyDelete = async () => {
    if (!confirmPurge) return;
    try {
      await api.delete(`/admin/users/${confirmPurge.id}/permanent`);
      toast.success(`@${confirmPurge.username} permanently deleted`);
      setConfirmPurge(null);
      loadTrash();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const trashDaysLeft = (deletedAt) => {
    if (!deletedAt) return "—";
    const del = new Date(deletedAt);
    const purge = new Date(del.getTime() + 30 * 86400 * 1000);
    const days = Math.max(0, Math.ceil((purge - new Date()) / 86400000));
    return `${days}d`;
  };

  const createInvite = async () => {
    try {
      await api.post("/invites", { code: newInvite.code || undefined, max_uses: Number(newInvite.max_uses) || 1 });
      toast.success("Invite created");
      setNewInvite({ code: "", max_uses: 1 });
      loadInvites();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const revokeInvite = async (id) => {
    try { await api.post(`/invites/${id}/revoke`); toast.success("Revoked"); loadInvites(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const deleteInvite = async (id, code) => {
    if (!window.confirm(`Permanently delete invite ${code}?\n\nThis cannot be undone.`)) return;
    try { await api.delete(`/admin/invites/${id}`); toast.success("Deleted"); loadInvites(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const removeListing = async (id) => {
    try { await api.delete(`/admin/listings/${id}`); toast.success("Removed"); loadListings(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const reactivateListing = async (id) => {
    try { await api.post(`/admin/listings/${id}/reactivate`); toast.success("Reactivated"); loadListings(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const permanentlyDeleteListing = async (id, title) => {
    if (!window.confirm(`Permanently delete "${title}"?\n\nThis cannot be undone.`)) return;
    try { await api.delete(`/admin/listings/${id}/permanent`); toast.success("Deleted"); loadListings(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const resolveReport = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); toast.success("Resolved"); loadReports(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const createAccessCode = async () => {
    try {
      const payload = {};
      if (newAccess.code.trim()) payload.code = newAccess.code.trim().toUpperCase();
      if (newAccess.max_uses !== "" && newAccess.max_uses !== null) {
        payload.max_uses = Number(newAccess.max_uses);
      }
      if (newAccess.note.trim()) payload.note = newAccess.note.trim();
      await api.post("/admin/access-codes", payload);
      toast.success("Access code created");
      setNewAccess({ code: "", max_uses: "", note: "" });
      loadAccessCodes();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const revokeAccessCode = async (id) => {
    try { await api.post(`/admin/access-codes/${id}/revoke`); toast.success("Revoked"); loadAccessCodes(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const deleteAccessCode = async (id) => {
    if (!window.confirm("Delete this access code permanently?")) return;
    try { await api.delete(`/admin/access-codes/${id}`); toast.success("Deleted"); loadAccessCodes(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${text}`);
    } catch { toast.error("Copy failed"); }
  };

  const approveApplication = async (userId, username) => {
    try {
      await api.post(`/admin/vendor-applications/${userId}/approve`);
      toast.success(`@${username} approved — now a Tier 1 vendor`);
      loadApplications();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitReject = async () => {
    if (!rejectApp) return;
    try {
      await api.post(`/admin/vendor-applications/${rejectApp.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success(`@${rejectApp.username} rejected`);
      setRejectApp(null);
      setRejectReason("");
      loadApplications();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitResolveDispute = async () => {
    if (!resolveDispute) return;
    if (resolveForm.resolution.trim().length < 3) { toast.error("Add a resolution note"); return; }
    try {
      await api.post(`/admin/transactions/${resolveDispute.id}/resolve`, {
        outcome: resolveForm.outcome,
        resolution: resolveForm.resolution.trim(),
      });
      toast.success("Dispute resolved");
      setResolveDispute(null);
      setResolveForm({ outcome: "completed", resolution: "" });
      loadDisputes();
      loadStats();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <div className="font-mono text-xs text-[#FFD700] tracking-widest uppercase">→ Admin</div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Hunt57 Control</h1>
        <span className={`h57-chip ${isSuper ? "h57-chip-gold" : ""}`} data-testid="admin-role-badge">
          {isSuper ? "Super Admin" : "Admin"}
        </span>
      </div>

      <div className="mt-6 flex gap-2 flex-wrap border-b border-white/10 pb-3">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t}`}
            className={`h57-chip !py-2 !px-3 uppercase ${tab === t ? "!border-[#FFD700] !text-[#FFD700]" : ""} ${t === "trash" ? "!border-red-500/40" : ""}`}
          >
            {t}
            {t === "trash" && stats?.trashed_users > 0 && (
              <span className="ml-2 bg-red-500/20 text-red-400 px-1.5 py-0.5 text-[10px] font-mono">{stats.trashed_users}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "stats" && stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Users", stats.total_users],
            ["Active Listings", stats.active_listings],
            ["Sold", stats.sold_listings],
            ["Open Reports", stats.open_reports],
            ["Pending Invite Reqs", stats.pending_invite_requests],
            ["Pending Vendor Apps", stats.pending_vendor_applications],
            ["Open Disputes", stats.open_disputes],
            ["Total Transactions", stats.total_transactions],
          ].map(([k, v]) => (
            <div key={k} className="h57-surface p-4">
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{k}</div>
              <div className="font-display font-black text-3xl mt-1 text-[#FF5F1F]">{v}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="mt-6 space-y-4">
          <div className="flex gap-2">
            <input className="h57-input" placeholder="Search username, email…" data-testid="admin-user-search" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers()} />
            <button onClick={loadUsers} className="h57-btn-secondary !py-3" data-testid="admin-user-search-btn">Search</button>
          </div>
          <div className="h57-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
                <tr>
                  <th className="text-left p-3">@user</th>
                  <th className="text-left p-3 hidden md:table-cell">Email</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Vendor / Tier</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5">
                    <td className="p-3 font-mono text-xs">@{u.username}</td>
                    <td className="p-3 hidden md:table-cell text-zinc-400 text-xs">{u.email}</td>
                    <td className="p-3"><span className="h57-chip">{u.account_status}</span></td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        {u.role === "admin" && (
                          <span className={`h57-chip ${u.is_super_admin ? "h57-chip-gold" : ""}`}>
                            {u.is_super_admin ? "Super Admin" : "Admin"}
                          </span>
                        )}
                        {u.is_vendor
                          ? <span className={`h57-chip ${u.vendor_tier === 3 ? "h57-chip-gold" : "h57-chip-orange"}`}>Vendor T{u.vendor_tier || 1}</span>
                          : <span className="h57-chip">Member</span>}
                        {u.founding_member && <span className="h57-chip h57-chip-gold">Founding</span>}
                        {(u.qualifying_sales_count || 0) > 0 && (
                          <span className="font-mono text-[10px] text-zinc-500">{u.qualifying_sales_count} sales</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right space-x-1 space-y-1">
                      {/* Super-admin only: role management */}
                      {isSuper && u.id !== me?.id && (
                        u.role !== "admin" ? (
                          <button onClick={() => promoteToAdmin(u)} data-testid={`admin-promote-${u.id}`} className="h57-chip !py-1 !px-2 !border-[#FFD700]/50 !text-[#FFD700] hover:!border-[#FFD700]">
                            Make Admin
                          </button>
                        ) : (
                          <>
                            {u.is_super_admin ? (
                              <button onClick={() => demoteFromSuper(u)} data-testid={`admin-demote-super-${u.id}`} className="h57-chip !py-1 !px-2 !border-[#FFD700]/50 !text-[#FFD700] hover:!border-[#FFD700]">
                                Revoke Super
                              </button>
                            ) : (
                              <button onClick={() => promoteToSuper(u)} data-testid={`admin-promote-super-${u.id}`} className="h57-chip !py-1 !px-2 !border-[#FFD700]/50 !text-[#FFD700] hover:!border-[#FFD700]">
                                Make Super
                              </button>
                            )}
                            <button onClick={() => demoteFromAdmin(u)} data-testid={`admin-demote-${u.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">
                              Revoke Admin
                            </button>
                          </>
                        )
                      )}
                      <button onClick={() => updateUser(u.id, { is_vendor: !u.is_vendor, ...(u.is_vendor ? {} : { vendor_tier: u.vendor_tier || 1, member_type: "vendor", membership_status: u.membership_status === "none" ? "trial" : u.membership_status }) })} data-testid={`admin-vendor-${u.id}`} className="h57-chip !py-1 !px-2 hover:!border-[#FF5F1F]">
                        {u.is_vendor ? "Remove Vendor" : "Make Vendor"}
                      </button>
                      {u.is_vendor && (
                        <select
                          value={u.vendor_tier || 1}
                          onChange={(e) => updateUser(u.id, { vendor_tier: Number(e.target.value) })}
                          data-testid={`admin-tier-${u.id}`}
                          className="h57-chip !py-1 !px-2 bg-transparent"
                          title="Set vendor tier"
                        >
                          <option value={1}>T1</option>
                          <option value={2}>T2</option>
                          <option value={3}>T3</option>
                        </select>
                      )}
                      <button onClick={() => updateUser(u.id, { founding_member: !u.founding_member, membership_status: u.founding_member ? "trial" : "lifetime_free" })} data-testid={`admin-founding-${u.id}`} className="h57-chip !py-1 !px-2 hover:!border-[#FFD700]">
                        {u.founding_member ? "Unmark Founding" : "Mark Founding"}
                      </button>
                      {u.account_status === "active" ? (
                        <button onClick={() => updateUser(u.id, { account_status: "suspended" })} data-testid={`admin-suspend-${u.id}`} className="h57-chip !py-1 !px-2 hover:!border-red-500">Suspend</button>
                      ) : (
                        <button onClick={() => updateUser(u.id, { account_status: "active" })} data-testid={`admin-reinstate-${u.id}`} className="h57-chip !py-1 !px-2 hover:!border-[#FF5F1F]">Reinstate</button>
                      )}
                      {(u.strikes_active || 0) > 0 && (
                        <button
                          onClick={() => clearStrikes(u.id, u.username, u.strikes_active)}
                          data-testid={`admin-clear-strikes-${u.id}`}
                          title={`Clear ${u.strikes_active} active strike${u.strikes_active === 1 ? "" : "s"}`}
                          className="h57-chip !py-1 !px-2 hover:!border-[#FFD700] hover:!text-[#FFD700] inline-flex items-center gap-1"
                        >
                          <ShieldCheck size={12} weight="fill" /> Clear {u.strikes_active} Strike{u.strikes_active === 1 ? "" : "s"}
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <button onClick={() => softDeleteUser(u.id, u.username)} data-testid={`admin-delete-${u.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500 hover:!text-red-300">Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "trash" && (
        <div className="mt-6 space-y-4">
          <div className="h57-surface p-4 border-red-500/30">
            <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">→ Trash Bin</div>
            <p className="mt-2 text-sm text-zinc-400">
              Deleted users stay here for <span className="text-white font-semibold">30 days</span>, then are automatically purged along with all their listings, messages, watchlist, and reports. You can restore anytime before then — or force a permanent purge below.
            </p>
          </div>
          <div className="h57-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
                <tr>
                  <th className="text-left p-3">@user</th>
                  <th className="text-left p-3 hidden md:table-cell">Email</th>
                  <th className="text-left p-3">Deleted</th>
                  <th className="text-left p-3">Purges In</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trash.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`trash-row-${u.id}`}>
                    <td className="p-3 font-mono text-xs">@{u.username}</td>
                    <td className="p-3 hidden md:table-cell text-zinc-400 text-xs">{u.email}</td>
                    <td className="p-3 text-zinc-400 text-xs">{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3">
                      <span className="h57-chip">{trashDaysLeft(u.deleted_at)}</span>
                    </td>
                    <td className="p-3 text-right space-x-1">
                      <button onClick={() => restoreUser(u.id, u.username)} data-testid={`admin-restore-${u.id}`} className="h57-chip !py-1 !px-2 hover:!border-[#FF5F1F] hover:!text-[#FF5F1F]">Restore</button>
                      <button onClick={() => setConfirmPurge(u)} data-testid={`admin-purge-${u.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">Purge Forever</button>
                    </td>
                  </tr>
                ))}
                {trash.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">Trash is empty</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "invites" && (
        <div className="mt-6 space-y-4">
          <div className="h57-surface p-4">
            <h3 className="font-display font-bold uppercase text-xs tracking-widest text-zinc-400 mb-3">Create Invite</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <input className="h57-input" placeholder="CODE (auto if blank)" data-testid="admin-invite-code" value={newInvite.code} onChange={(e) => setNewInvite({ ...newInvite, code: e.target.value })} />
              <input type="number" min="1" className="h57-input" placeholder="Max uses" data-testid="admin-invite-max" value={newInvite.max_uses} onChange={(e) => setNewInvite({ ...newInvite, max_uses: e.target.value })} />
              <button onClick={createInvite} className="h57-btn-primary" data-testid="admin-invite-create">Create</button>
            </div>
          </div>
          <div className="h57-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
                <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Uses</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id} className="border-b border-white/5">
                    <td className="p-3 font-mono text-xs">{i.code}</td>
                    <td className="p-3">{(i.used_by?.length || 0)}/{i.max_uses}</td>
                    <td className="p-3"><span className={`h57-chip ${i.status === "revoked" ? "!border-red-500/50 !text-red-400" : ""}`}>{i.status}</span></td>
                    <td className="p-3 text-right space-x-1">
                      {i.status === "active" && (
                        <button onClick={() => revokeInvite(i.id)} data-testid={`admin-revoke-${i.id}`} className="h57-chip !py-1 !px-2 hover:!border-red-500">Revoke</button>
                      )}
                      {i.status !== "active" && (
                        <button onClick={() => deleteInvite(i.id, i.code)} data-testid={`admin-delete-invite-${i.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "requests" && (
        <div className="mt-6 h57-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3 hidden md:table-cell">Reason</th><th className="text-left p-3">Status</th></tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="p-3">{r.name}</td>
                  <td className="p-3 text-zinc-400 text-xs">{r.email}</td>
                  <td className="p-3 hidden md:table-cell text-zinc-400 text-xs max-w-md truncate">{r.reason}</td>
                  <td className="p-3"><span className="h57-chip">{r.status}</span></td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">No requests yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "listings" && (
        <div className="mt-6 h57-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
              <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Seller</th><th className="text-left p-3">Price</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {listings.map((l) => (
                <tr key={l.id} className="border-b border-white/5">
                  <td className="p-3 max-w-xs truncate">{l.title}</td>
                  <td className="p-3 font-mono text-xs">@{l.seller_username}</td>
                  <td className="p-3">${Number(l.price).toFixed(2)}</td>
                  <td className="p-3"><span className={`h57-chip ${l.status === "removed" ? "!border-red-500/50 !text-red-400" : ""}`}>{l.status}</span></td>
                  <td className="p-3 text-right space-x-1">
                    {l.status !== "removed" && (
                      <button onClick={() => removeListing(l.id)} data-testid={`admin-remove-listing-${l.id}`} className="h57-chip !py-1 !px-2 hover:!border-red-500">Remove</button>
                    )}
                    {l.status === "removed" && (
                      <>
                        <button onClick={() => reactivateListing(l.id)} data-testid={`admin-reactivate-listing-${l.id}`} className="h57-chip !py-1 !px-2 !border-emerald-500/50 !text-emerald-400 hover:!border-emerald-500">Reactivate</button>
                        <button onClick={() => permanentlyDeleteListing(l.id, l.title)} data-testid={`admin-delete-listing-${l.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "access" && (
        <AdminAccessTab
          siteConfig={siteConfig}
          accessCodes={accessCodes}
          newAccess={newAccess}
          setNewAccess={setNewAccess}
          setLandingMode={setLandingMode}
          toggleNotifyNewUsers={toggleNotifyNewUsers}
          createAccessCode={createAccessCode}
          revokeAccessCode={revokeAccessCode}
          deleteAccessCode={deleteAccessCode}
          copyToClipboard={copyToClipboard}
        />
      )}

      {tab === "applications" && (
        <div className="mt-6 space-y-4" data-testid="admin-applications-list">
          <div className="h57-surface p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Vendor Applications</div>
            <p className="mt-2 text-sm text-zinc-400">
              Pending applications from members who want vendor status but aren&apos;t using a referral voucher.
              Approving grants Tier 1 vendor (25 listing cap / $50 max).
            </p>
          </div>
          {applications.length === 0 ? (
            <div className="h57-surface p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
              No pending applications
            </div>
          ) : (
            applications.map((u) => (
              <div key={u.id} className="h57-surface p-4" data-testid={`application-row-${u.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-mono text-xs">@{u.username} · {u.email}</div>
                    <div className="font-display font-bold mt-1">{u.display_name || u.username}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Submitted {u.vendor_application?.submitted_at ? new Date(u.vendor_application.submitted_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveApplication(u.id, u.username)}
                      data-testid={`app-approve-${u.id}`}
                      className="h57-btn-primary text-xs"
                    >
                      Approve → Tier 1
                    </button>
                    <button
                      onClick={() => { setRejectApp(u); setRejectReason(""); }}
                      data-testid={`app-reject-${u.id}`}
                      className="text-xs inline-flex items-center gap-1 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono uppercase tracking-widest py-2 px-3"
                    >
                      Reject
                    </button>
                  </div>
                </div>
                {u.vendor_application && (
                  <div className="mt-3 space-y-2 text-sm">
                    {u.vendor_application.reason && (
                      <div><span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Reason: </span>{u.vendor_application.reason}</div>
                    )}
                    {u.vendor_application.experience && (
                      <div><span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Experience: </span>{u.vendor_application.experience}</div>
                    )}
                    {u.vendor_application.social && (
                      <div><span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Social: </span>{u.vendor_application.social}</div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "disputes" && (
        <div className="mt-6 space-y-4" data-testid="admin-disputes-list">
          <div className="h57-surface p-4 border-red-500/30">
            <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">→ Open Disputes</div>
            <p className="mt-2 text-sm text-zinc-400">
              Transactions flagged by either party. Resolve with an outcome — <span className="text-[#FF5F1F]">completed</span> counts
              the sale (and applies feedback), <span className="text-zinc-400">cancelled</span> nullifies it.
            </p>
          </div>
          {disputes.length === 0 ? (
            <div className="h57-surface p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
              No open disputes
            </div>
          ) : (
            disputes.map((t) => (
              <div key={t.id} className="h57-surface p-4 border-red-500/30" data-testid={`dispute-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">
                      Opened by {t.disputed_by} · {t.disputed_at ? new Date(t.disputed_at).toLocaleString() : "—"}
                    </div>
                    <div className="font-display font-bold mt-1">{t.listing_title || "(listing removed)"}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Seller @{t.seller_username} ↔ Buyer @{t.buyer_username} · ${Number(t.sale_price || 0).toFixed(2)}
                    </div>
                    <div className="mt-2 text-sm text-red-300">{t.dispute_reason}</div>
                    <div className="mt-2 text-xs text-zinc-500">
                      Buyer confirmed: {t.buyer_confirmed_at ? "✓" : "—"} · Seller confirmed: {t.seller_confirmed_at ? "✓" : "—"}
                      {t.feedback_stars ? ` · Feedback: ${t.feedback_stars}★` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => { setResolveDispute(t); setResolveForm({ outcome: "completed", resolution: "" }); }}
                    data-testid={`dispute-resolve-${t.id}`}
                    className="h57-btn-primary text-xs"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "audit" && (
        <AdminAuditTab
          auditLog={auditLog}
          auditSalesOnly={auditSalesOnly}
          setAuditSalesOnly={setAuditSalesOnly}
        />
      )}

      {tab === "vault" && isSuper && (
        <AdminVaultTab
          vault={vault}
          vaultFilter={vaultFilter}
          setVaultFilter={setVaultFilter}
          loadVault={loadVault}
          wipeUserHistory={wipeUserHistory}
          deleteTransaction={deleteTransaction}
        />
      )}

      {tab === "contest" && <AdminContest />}

      {tab === "messages" && <AdminMessages />}

      {tab === "ads" && <AdminAds />}

      {tab === "reports" && (
        <div className="mt-6 h57-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
              <tr><th className="text-left p-3">Type</th><th className="text-left p-3">Reason</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="p-3">{r.target_type}</td>
                  <td className="p-3 text-zinc-400 text-xs">{r.reason}</td>
                  <td className="p-3"><span className="h57-chip">{r.status}</span></td>
                  <td className="p-3 text-right">
                    {r.status === "open" && <button onClick={() => resolveReport(r.id)} data-testid={`admin-resolve-${r.id}`} className="h57-chip !py-1 !px-2 hover:!border-[#FF5F1F]">Resolve</button>}
                  </td>
                </tr>
              ))}
              {reports.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">No reports</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {confirmPurge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setConfirmPurge(null)}>
          <div className="w-full max-w-md bg-[#121212] border border-red-500/40 p-6" onClick={(e) => e.stopPropagation()} data-testid="purge-confirm-modal">
            <div className="font-mono text-xs uppercase tracking-widest text-red-400">→ Permanent Deletion</div>
            <h3 className="mt-2 font-display font-black uppercase text-2xl">Purge @{confirmPurge.username}?</h3>
            <div className="mt-4 space-y-2 text-sm text-zinc-300">
              <p>This <span className="text-red-400 font-bold">cannot be undone</span>. The following will be permanently deleted from Hunt57:</p>
              <ul className="pl-5 list-disc text-zinc-400 space-y-1 text-xs">
                <li>User account (<span className="font-mono">{confirmPurge.email}</span>)</li>
                <li>All of their listings (active, sold, removed)</li>
                <li>All messages they&apos;ve sent</li>
                <li>All conversations they participated in</li>
                <li>Their watchlist</li>
                <li>Reports they filed</li>
              </ul>
              <p className="pt-2 text-zinc-500 text-xs">
                Prefer to keep the record? Cancel and let the 30-day auto-purge handle it instead.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirmPurge(null)} data-testid="purge-cancel" className="h57-btn-secondary flex-1">Cancel</button>
              <button onClick={permanentlyDelete} data-testid="purge-confirm" className="h57-btn-primary flex-1 !bg-red-600 !border-red-600 hover:!bg-red-500">
                Purge Forever
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reject application modal */}
      {rejectApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setRejectApp(null)}>
          <div className="w-full max-w-md bg-[#121212] border border-red-500/40 p-6" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <div className="font-mono text-xs uppercase tracking-widest text-red-400">→ Reject Application</div>
            <h3 className="mt-2 font-display font-black uppercase text-xl">@{rejectApp.username}</h3>
            <label className="h57-label mt-4">Reason (optional — shown to applicant)</label>
            <textarea className="h57-input min-h-[80px]" data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500} />
            <div className="mt-6 flex gap-3">
              <button onClick={() => setRejectApp(null)} data-testid="reject-cancel" className="h57-btn-secondary flex-1">Cancel</button>
              <button onClick={submitReject} data-testid="reject-submit" className="h57-btn-primary flex-1 !bg-red-600 !border-red-600 hover:!bg-red-500">Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve dispute modal */}
      {resolveDispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setResolveDispute(null)}>
          <div className="w-full max-w-md bg-[#121212] border border-[#FFD700]/40 p-6" onClick={(e) => e.stopPropagation()} data-testid="resolve-dispute-modal">
            <div className="font-mono text-xs uppercase tracking-widest text-[#FFD700]">→ Resolve Dispute</div>
            <h3 className="mt-2 font-display font-black uppercase text-lg">{resolveDispute.listing_title || "(listing removed)"}</h3>
            <div className="text-xs text-zinc-500 mt-1">
              @{resolveDispute.buyer_username} ↔ @{resolveDispute.seller_username}
            </div>
            <label className="h57-label mt-4">Outcome</label>
            <select
              className="h57-input"
              data-testid="resolve-outcome"
              value={resolveForm.outcome}
              onChange={(e) => setResolveForm({ ...resolveForm, outcome: e.target.value })}
            >
              <option value="completed">Completed — sale stands (feedback applies)</option>
              <option value="cancelled">Cancelled — nullify the sale</option>
            </select>
            <label className="h57-label mt-4">Resolution note</label>
            <textarea
              className="h57-input min-h-[80px]"
              data-testid="resolve-note"
              value={resolveForm.resolution}
              onChange={(e) => setResolveForm({ ...resolveForm, resolution: e.target.value })}
              maxLength={1000}
              placeholder="Explain your decision so both parties can see it."
            />
            <div className="mt-6 flex gap-3">
              <button onClick={() => setResolveDispute(null)} data-testid="resolve-cancel" className="h57-btn-secondary flex-1">Cancel</button>
              <button onClick={submitResolveDispute} data-testid="resolve-submit" className="h57-btn-primary flex-1">Resolve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
